import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
import { Component, OnInit, OnDestroy, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SankhyaGatewayService } from '../../services/sankhya-gateway.service';
import { AuthService } from '../../services/auth.service';
import { EmpresaStateService } from '../../services/empresa-state.service';
import { PermissaoAcessoService } from '../../services/permissao-acesso.service';
import { AerosysGatewayService, AerosysEmpresa, AerosysVendaSeparacao } from '../../services/aerosys-gateway.service';
import { Subscription } from 'rxjs';
import { SeparacaoLinha } from '../paineis-separacao/paineis-separacao.component';

@Component({
  selector: 'app-estoque-separacao',
  standalone: true,
  imports: [LoadingIndicatorComponent, CommonModule, FormsModule],
  templateUrl: './estoque-separacao.component.html',
  styleUrls: ['./estoque-separacao.component.css']
})
export class EstoqueSeparacaoComponent implements OnInit, OnDestroy {
  pedidos: SeparacaoLinha[] = [];
  carregando = false;
  erro = '';
  private sub?: Subscription;
  private _empresaSub?: Subscription;

  @Output() iniciar = new EventEmitter<number>();
  /** "Continuar Separação" clicado num card do Sistema Legado (Aerosys) — emite
   *  o id real da venda no Aerosys (recuperado de -Math.abs(nunota), ver
   *  _mapLegadoParaLinha) e o número exibido, para a tela de itens legado. */
  @Output() iniciarLegado = new EventEmitter<{ idVenda: number; numVenda?: string }>();
  @ViewChild('tagInput') tagInputRef?: ElementRef<HTMLInputElement>;

  iniciandoNunota: number | null = null;
  erroIniciar = '';
  codusuAtual = 0;

  // ── Estornar para ABERTO ─────────────────────────────────
  estornandoNunota: number | null = null;
  erroEstornar = '';
  confirmandoEstorno: SeparacaoLinha | null = null;

  // ── Modal "Informar TAG" — obrigatório antes de iniciar a separação ──
  modalTagAberto = false;
  pedidoParaIniciar: SeparacaoLinha | null = null;
  numTagDigitada = '';
  erroModalTag = '';

  // ── Vendas do Sistema Legado (Aerosys) — fonte alternativa dos cards ────
  // Liberado por permissão própria (VERAERO no resource GPL_SeparacaoEstoque
  // — diferente da EDTAERO do Painel de Separação, cada tela com sua sigla).
  //
  // NÃO é um toggle manual: quem TEM a permissão VERAERO vê SOMENTE os cards
  // das vendas Autorizado/Separação do Aerosys aqui; quem NÃO tem vê SOMENTE
  // os pedidos do Sankhya, como sempre foi (mesmo padrão já usado no Painel
  // de Separação — ver paineis-separacao.component.ts).
  //
  // Por enquanto essa seção é só de VISUALIZAÇÃO: os cards mostram vendedor,
  // separador, início e progresso igual aos do Sankhya, mas o botão "Iniciar
  // Separação" fica desabilitado (a execução real da separação de vendas do
  // Aerosys ainda depende de uma tabela de progresso própria, que não foi
  // construída nesta etapa — ver GPL_SEPARACAO_ITENS como referência de
  // padrão quando isso for implementado).
  podeVerVendasLegado = false;
  legadoAberto = false;
  legadoEmpresas: AerosysEmpresa[] = [];
  legadoEmpresasCarregando = false;
  legadoEmpresasErro = '';
  legadoEmpresaSelecionada: AerosysEmpresa | null = null;
  legadoLista: AerosysVendaSeparacao[] = [];
  legadoCarregando = false;
  legadoErro = '';

  /** Mesma lista de empresas do Aerosys usadas em "Gerar Venda a Partir de
   *  Sistema Legado" (gateway.component.ts) — é a mesma fonte de dados. */
  private readonly LEGADO_EMPRESAS_PERMITIDAS = [1, 3, 5, 7, 8, 9, 13, 15, 17];
  private readonly LEGADO_PREF_STORAGE_KEY = 'gpl_estoque_separacao_legado_pref';

  /** Fonte de dados dos cards: com a permissão VERAERO, SOMENTE vendas do
   *  Aerosys; sem a permissão, SOMENTE pedidos do Sankhya — nunca as duas
   *  juntas (mesma regra do Painel de Separação). */
  get linhas(): SeparacaoLinha[] {
    if (this.legadoAberto) {
      return this.legadoLista.map(v => this._mapLegadoParaLinha(v));
    }
    return this.pedidos;
  }

  /** Loading da fonte ativa no momento (Sankhya ou Aerosys). */
  get carregandoLinhas(): boolean {
    return this.legadoAberto ? this.legadoCarregando : this.carregando;
  }

  /** Enquanto o usuário ainda está escolhendo a empresa do Aerosys, não faz
   *  sentido mostrar "fila limpa" embaixo. */
  get aguardandoEscolhaEmpresaLegado(): boolean {
    return this.legadoAberto && !this.legadoEmpresaSelecionada;
  }

  constructor(
    private gateway: SankhyaGatewayService,
    private auth: AuthService,
    private aerosysGateway: AerosysGatewayService,
    private permissaoAcesso: PermissaoAcessoService,
    readonly empresaState: EmpresaStateService
  ) {}

  async ngOnInit(): Promise<void> {
    this.codusuAtual = await this.auth.getCodusuNumerico();
    // Carregamento inicial (exibe o loading). Sem polling periódico — esta
    // tela é recriada pelo *ngIf do gateway toda vez que o usuário navega
    // até ela (menu, ou voltando/finalizando de dentro de uma separação),
    // então o próprio ngOnInit já garante a lista sempre atualizada nesses
    // momentos, sem precisar ficar atualizando sozinha em segundo plano.
    this.buscarAutorizados(true);

    // Isolamento por empresa: recarrega ao trocar a empresa selecionada
    // (mesmo padrão do EmpresaStateService usado em Pedido de Venda).
    this._empresaSub = this.empresaState.empresaMudou.subscribe(() => {
      this.buscarAutorizados(true);
    });

    this.permissaoAcesso.podeVerVendasLegadoAutorizado().then(pode => {
      this.podeVerVendasLegado = pode;
      // A fonte dos cards segue direto a permissão — quem tem VERAERO já abre
      // vendo o Sistema Legado, sem precisar marcar nada (ver ngOnInit no
      // Painel de Separação, mesmo padrão).
      this.legadoAberto = pode;
      if (pode) {
        this.carregarLegadoEmpresas();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.sub) this.sub.unsubscribe();
    if (this._empresaSub) this._empresaSub.unsubscribe();
  }

  /** Nome de exibição da empresa atualmente selecionada (badge no cabeçalho). */
  get nomeEmpresaAtual(): string {
    const emp = this.empresaState.opcoes.find(e => e.codEmp === this.empresaState.selecionada);
    return emp?.nomeFant || emp?.razaoAbrev || '';
  }

  /** AOG (vermelho), Urgente (laranja), Alta (vermelho claro) e Normal (verde) --
   *  mesma lógica de cores do Painel de Separação (ver prioridadeClass lá).
   *  Usa "includes" porque o valor cru às vezes vem com prefixo numérico do
   *  Sankhya, ex.: "1-AOG", "3-URGENTE". */
  prioridadeClass(prioridade: string | null | undefined): string {
    const p = (prioridade ?? '').trim().toLowerCase();
    if (p.includes('aog')) return 'badge-aog';
    if (p.includes('urgente')) return 'badge-urgente';
    if (p.includes('alta')) return 'badge-alta';
    return 'badge-normal';
  }

  buscarAutorizados(exibirLoading: boolean = false): void {
    if (exibirLoading) {
      this.carregando = true;
      this.erro = '';
    }

    if (this.sub && !this.sub.closed) {
      this.sub.unsubscribe();
    }

    const codEmp = Number(this.empresaState.selecionada ?? 1);
    this.sub = this.gateway.buscarPedidosSeparacao(this.codusuAtual, codEmp).subscribe({
      next: (res: any) => {
        this.carregando = false;
        if (res.sucesso) {
          this.erro = '';
          // Backend já filtra: AUTORIZADO (todos) + SEPARACAO do usuário logado
          this.pedidos = res.pedidos as SeparacaoLinha[];
        } else {
          if (exibirLoading || !this.pedidos.length) {
            this.erro = res.erro || 'Erro ao carregar pedidos.';
          }
        }
      },
      error: (err) => {
        this.carregando = false;
        if (exibirLoading || !this.pedidos.length) {
          this.erro = 'Falha de comunicação com o servidor.';
        }
        console.error(err);
      }
    });
  }

  /** Em separação por MIM (dono da separação ativa) — mostra "Continuar Separação".
   *  Para vendas do Aerosys NÃO há validação de posse (decisão explícita do
   *  cliente): qualquer usuário pode continuar uma separação do Sistema
   *  Legado já iniciada por outra pessoa, então aqui basta estar em
   *  SEPARAÇÃO — não importa quem apareça como sepNome. */
  emSeparacao(pedido: SeparacaoLinha): boolean {
    if (pedido.origem === 'aerosys') {
      return this.emSeparacaoPorAlguem(pedido);
    }
    return this.emSeparacaoPorAlguem(pedido) && pedido.sepCodusu === this.codusuAtual;
  }

  /** Em separação por QUALQUER separador (independente de quem está vendo a tela) —
   *  usado pra exibir as infos (separador/início/TAG/progresso) mesmo quando não é minha. */
  emSeparacaoPorAlguem(pedido: SeparacaoLinha): boolean {
    const st = pedido.status?.toUpperCase() || '';
    return st === 'SEPARACAO' || st === 'SEPARAÇÃO';
  }

  /** Em separação, mas por OUTRO separador — inclui a visão gerencial (usuário SUP,
   *  codusu=0), onde nenhuma separação em andamento é "minha" (sepCodusu nunca é 0).
   *  Sem posse da separação (ver validarPosseSeparacao no backend), então aqui a tela
   *  só mostra informação — nada de oferecer "Continuar" pra algo que não é meu. */
  separacaoDeOutro(pedido: SeparacaoLinha): boolean {
    return this.emSeparacaoPorAlguem(pedido) && !this.emSeparacao(pedido);
  }

  /** Pode estornar para ABERTO quando ainda não começou (AUTORIZADO) ou está
   *  em separação POR MIM — não permite estornar a separação de outro
   *  separador direto da tela pra evitar derrubar o trabalho de outra pessoa
   *  sem ela saber. Vendas do Aerosys nunca podem ser estornadas por aqui —
   *  essa ação chama o backend do Sankhya, que não sabe nada sobre elas. */
  podeEstornar(pedido: SeparacaoLinha): boolean {
    return pedido.origem !== 'aerosys' && !this.separacaoDeOutro(pedido);
  }

  solicitarEstornar(event: Event, pedido: SeparacaoLinha): void {
    event.stopPropagation();
    if (pedido.origem === 'aerosys') return; // ver podeEstornar — não deveria nem chegar aqui.
    if (this.estornandoNunota !== null) return;
    this.confirmandoEstorno = pedido;
    this.erroEstornar = '';
  }

  cancelarEstornar(): void {
    if (this.estornandoNunota !== null) return;
    this.confirmandoEstorno = null;
    this.erroEstornar = '';
  }

  confirmarEstornar(): void {
    const pedido = this.confirmandoEstorno;
    if (!pedido || this.estornandoNunota !== null) return;
    this.estornandoNunota = pedido.nunota;
    this.erroEstornar = '';
    this.gateway.estornarSeparacao(pedido.nunota).subscribe({
      next: (res: any) => {
        this.estornandoNunota = null;
        if (res?.sucesso) {
          this.confirmandoEstorno = null;
          this.buscarAutorizados(false);
        } else {
          this.erroEstornar = res?.erro || 'Erro ao estornar separação.';
        }
      },
      error: () => {
        this.estornandoNunota = null;
        this.erroEstornar = 'Falha de comunicação com o servidor.';
      }
    });
  }

  /** Clique em "Continuar Separação" — roteia para a tela certa conforme a origem
   *  do pedido: Sankhya (iniciar, nunota real) ou Sistema Legado (iniciarLegado,
   *  id da venda no Aerosys, recuperado do nunota sintético negativo). */
  continuarSeparacao(pedido: SeparacaoLinha): void {
    if (pedido.origem === 'aerosys') {
      this.iniciarLegado.emit({ idVenda: Math.abs(pedido.nunota), numVenda: pedido.numTituloLegado });
      return;
    }
    this.iniciar.emit(pedido.nunota);
  }

  /** Clique em "Iniciar Separação" — abre o modal de TAG em vez de chamar a API direto.
   *  A chamada real só acontece em confirmarInicioComTag(), depois que o usuário informa a TAG. */
  iniciarSeparacao(pedido: SeparacaoLinha): void {
    if (pedido.origem === 'aerosys') return; // botão fica desabilitado — ver template.
    if (this.iniciandoNunota !== null) return;
    this.pedidoParaIniciar = pedido;
    this.numTagDigitada = '';
    this.erroModalTag = '';
    this.modalTagAberto = true;
    // O input só existe no DOM depois do *ngIf renderizar — aguarda o próximo tick.
    setTimeout(() => this.tagInputRef?.nativeElement?.focus(), 0);
  }

  /** Mantém só dígitos e no máximo 2 caracteres — TAGs vão de 01 a 40. */
  onTagInputChange(valor: string): void {
    const limpo = (valor || '').replace(/\D/g, '').slice(0, 2);
    if (limpo !== this.numTagDigitada) {
      this.numTagDigitada = limpo;
    }
    if (this.erroModalTag) this.erroModalTag = '';
  }

  fecharModalTag(): void {
    if (this.iniciandoNunota !== null) return; // não fecha durante o request
    this.modalTagAberto = false;
    this.pedidoParaIniciar = null;
    this.numTagDigitada = '';
    this.erroModalTag = '';
  }

  /** Confirma o modal — só prossegue se a TAG foi informada. */
  confirmarInicioComTag(): void {
    const tag = (this.numTagDigitada || '').trim();
    if (!tag) {
      this.erroModalTag = 'Informe o número da TAG para continuar.';
      return;
    }
    const tagNum = Number(tag);
    if (!Number.isInteger(tagNum) || tagNum < 1 || tagNum > 40) {
      this.erroModalTag = 'TAG inválida — informe um número entre 01 e 40.';
      return;
    }
    const pedido = this.pedidoParaIniciar;
    if (!pedido || this.iniciandoNunota !== null) return;

    this.iniciandoNunota = pedido.nunota;
    this.erroIniciar = '';
    this.erroModalTag = '';
    this.gateway.iniciarSeparacao(pedido.nunota, tag).subscribe({
      next: (res: any) => {
        this.iniciandoNunota = null;
        if (res?.sucesso) {
          this.modalTagAberto = false;
          this.pedidoParaIniciar = null;
          this.numTagDigitada = '';
          this.iniciar.emit(pedido.nunota);
        } else {
          this.erroModalTag = res?.erro || 'Erro ao iniciar separação.';
        }
      },
      error: () => {
        this.iniciandoNunota = null;
        this.erroModalTag = 'Falha de comunicação com o servidor.';
      }
    });
  }

  // ── Vendas do Sistema Legado (Aerosys) — fonte alternativa dos cards ────
  // Bloco isolado de propósito (ver comentário no topo do arquivo) — todo ele
  // pode ser removido junto com a permissão VERAERO quando o Aerosys for
  // desativado, sem afetar o restante do Aplicativo de Separação.

  /** Converte uma venda "rica" do Aerosys (VendaSeparacaoDTO) para o mesmo
   *  formato de linha usado pelos pedidos do Sankhya — mesma função usada no
   *  Painel de Separação (paineis-separacao.component.ts#_mapLegadoParaLinha),
   *  duplicada aqui de propósito para manter as duas telas independentes e
   *  fáceis de remover isoladamente. */
  private _mapLegadoParaLinha(v: AerosysVendaSeparacao): SeparacaoLinha {
    const totalItens = v.totalItens ?? 0;
    const qtdSeparados = v.itensSeparados ?? 0;
    const imediatoTruthy = ['S', 'SIM', 'TRUE', '1'].includes((v.fatImediato ?? '').toUpperCase());
    return {
      nunota: -Math.abs(v.id),
      prioridade: v.prioridade || 'Normal',
      imediato: imediatoTruthy ? 'Sim' : 'Não',
      nomeCliente: v.clienteNome || '—',
      vendedor: v.vendedorNome || '—',
      status: v.status || '—',
      sepCodusu: 0, // nunca "meu" — força o card pro estado informativo (separacaoDeOutro).
      sepNome: v.separadorNome || '',
      qtdSeparados,
      totalItens,
      prefixo: v.prefixoAeronave || undefined, // AERONAVE.AER_MATRICULA via VENDA.AER_ID (ver VendaSeparacaoDTO).
      dataInicio: this._formatarDataBr(v.dataInicioSep, true),
      dtneg: this._formatarDataBr(v.dataEmissao, false),
      origem: 'aerosys',
      numTituloLegado: v.numTitulo || String(v.id),
      itensPendenciaResolvida: v.itensPendenciaResolvida ?? 0,
    };
  }

  /** Normaliza uma data (ISO ou epoch) para DD/MM/YYYY [HH:mm] — mesma lógica
   *  do Painel de Separação, duplicada aqui pelo mesmo motivo de isolamento. */
  private _formatarDataBr(valor: string | null | undefined, comHora: boolean): string | undefined {
    if (!valor) return undefined;
    const d = new Date(valor);
    if (isNaN(d.getTime())) return undefined;
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    if (!comHora) return `${dd}/${mm}/${yyyy}`;
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
  }

  carregarLegadoEmpresas(): void {
    this.legadoEmpresasCarregando = true;
    this.legadoEmpresasErro = '';
    this.aerosysGateway.listarEmpresas().subscribe({
      next: (lista) => {
        const todas = Array.isArray(lista) ? lista : [];
        this.legadoEmpresas = todas.filter(e => this.LEGADO_EMPRESAS_PERMITIDAS.includes(Number(e.id)));
        this.legadoEmpresasCarregando = false;

        // Ja nasce numa empresa: a lembrada (localStorage) ou, na falta dela,
        // a empresa 1 por padrao -- o usuario troca a qualquer momento pelo
        // seletor do cabecalho, sem precisar de card/tela à parte.
        const empresaLembrada = this._restaurarEmpresaLegado();
        let alvo = empresaLembrada != null
          ? this.legadoEmpresas.find(e => Number(e.id) === empresaLembrada)
          : undefined;
        if (!alvo) {
          alvo = this.legadoEmpresas.find(e => Number(e.id) === 1);
        }
        if (alvo) this.selecionarLegadoEmpresa(alvo, false);
      },
      error: (err: any) => {
        this.legadoEmpresasErro = err?.error?.detail ?? err?.message ?? 'Erro ao carregar empresas do Aerosys.';
        this.legadoEmpresasCarregando = false;
      }
    });
  }

  selecionarLegadoEmpresa(empresa: AerosysEmpresa, persistir = true): void {
    this.legadoEmpresaSelecionada = empresa;
    if (persistir) this._salvarEmpresaLegado(empresa.id);
    this.buscarVendasLegado();
  }

  onLegadoEmpresaSelectChange(id: number | string | null): void {
    if (id == null) return;
    const empresa = this.legadoEmpresas.find(e => Number(e.id) === Number(id));
    if (empresa && Number(empresa.id) !== Number(this.legadoEmpresaSelecionada?.id)) {
      this.selecionarLegadoEmpresa(empresa);
    }
  }

  buscarVendasLegado(): void {
    if (!this.legadoEmpresaSelecionada) return;
    this.legadoCarregando = true;
    this.legadoErro = '';

    this.aerosysGateway.listarSeparacaoPainel(this.legadoEmpresaSelecionada.id).subscribe({
      next: (lista) => {
        this.legadoLista = Array.isArray(lista) ? lista : [];
        this.legadoCarregando = false;
      },
      error: (err: any) => {
        this.legadoErro = err?.error?.detail ?? err?.message ?? 'Erro ao buscar Vendas do Sistema Legado.';
        this.legadoCarregando = false;
      }
    });
  }

  // ── Preferência persistida (localStorage) ────────────────────
  // A fonte (Sankhya x Aerosys) já é automática pela permissão VERAERO — só
  // resta lembrar QUAL empresa do Aerosys o usuário escolheu.

  private _salvarEmpresaLegado(empresaId: number | null): void {
    try {
      localStorage.setItem(this.LEGADO_PREF_STORAGE_KEY, JSON.stringify({ empresaId }));
    } catch {
      // localStorage indisponivel (modo privado etc.) - sem persistencia, sem quebrar a tela.
    }
  }

  private _restaurarEmpresaLegado(): number | null {
    try {
      const raw = localStorage.getItem(this.LEGADO_PREF_STORAGE_KEY);
      if (!raw) return null;
      const pref = JSON.parse(raw);
      return typeof pref?.empresaId === 'number' ? pref.empresaId : null;
    } catch {
      return null;
    }
  }
}
